import axios from 'axios';
import { IdleManager } from '@/utils/idle';

const baseURL = process.env.VUE_APP_API_BASE_URL || '';

const http = axios.create({
  baseURL,
});

// Attach Authorization header from localStorage token and signal activity
http.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth.token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Activity
  IdleManager.signalActivity();
  return config;
});

let isRefreshing = false;
let queue = [];

function enqueue(callback) {
  return new Promise((resolve, reject) => {
    queue.push({ resolve, reject, callback });
  });
}

function flush(error, token) {
  queue.forEach(({ resolve, reject, callback }) => {
    if (error) reject(error);
    else resolve(callback(token));
  });
  queue = [];
}

async function refreshAccessToken() {
  const token = localStorage.getItem('auth.token');
  const res = await axios.post(`${baseURL}/api/v1/auth/refresh`, {}, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const newToken = res.data?.access_token;
  if (!newToken) throw new Error('No access token returned');
  localStorage.setItem('auth.token', newToken);
  return newToken;
}

http.interceptors.response.use(
  (resp) => {
    // Activity on successful response
    IdleManager.signalActivity();
    return resp;
  },
  async (error) => {
    const original = error.config || {};
    const status = error?.response?.status;
    const isRefresh = original.url?.includes('/api/v1/auth/refresh');

    if (status === 401 && !isRefresh && !original._retry) {
      original._retry = true;
      if (isRefreshing) {
        return enqueue((token) => {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${token}`;
          return http(original);
        });
      }
      try {
        isRefreshing = true;
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        flush(null, newToken);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return http(original);
      } catch (e) {
        isRefreshing = false;
        flush(e, null);
        // Clear and redirect to login
        try {
          localStorage.removeItem('auth.token');
          localStorage.removeItem('auth.user');
        } catch {
          // Silently ignore localStorage errors (e.g., private browsing mode)
        }
        window.location.assign('/pam/login');
        return Promise.reject(e);
      }
    }

    return Promise.reject(error);
  }
);

export default http;
